'use client'

import AgentEmailButton from '@component/card/agent/button/email'
import CloseButton from '@component/closeButton'
import { ResponsiveImageBgLoading } from '@component/imageBgLoading'
import useGallery from '@hooks/useGallery'
import { isSaleData, isVillageData } from '@utils/house'
import classNames from 'classnames'
import { find, get, isEqual, map, size } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useList, useUnmount } from 'react-use'
import { Waypoint } from 'react-waypoint'
import VideoPlayerContent from '../videoPlayer/content'
import GalleryAside from './aside'
import GallerySlide from './slide'

export default function HouseGalleryContent() {
  const {
    house,
    currentTag,
    showThreeD,
    showVideo,
    hideGallery,
    videoUrl,
    threeDUrl
  } = useGallery()

  const agent = get(house, 'agents.0', null)
  const isSale = isSaleData(house)
  const isVillage = isVillageData(house)

  const contentRef = useRef(null)

  useUnmount(() => {
    hideGallery()
  })

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex-1 min-h-0 flex flex-col'>
        {
          isSale || isVillage
            ? (
              <div className={classNames('shadow-card flex items-center px-10 md:px-20 relative sm-s:landscape:hidden')}>
                <TopBar></TopBar>
                <CloseButton onClick={hideGallery} className='absolute right-10 top-28 lg:static'></CloseButton>
              </div>
            )
            : (
              <CloseButton className='absolute right-6 top-6 z-10' onClick={hideGallery}></CloseButton>
            )
        }
        <div className='flex-1 min-h-0 grid grid-cols-1 grid-rows-[1fr,auto] p-10 md:p-20 md:grid-cols-[1fr,auto] overflow-auto relative' ref={contentRef}>
          {
            showThreeD && (
              <div className='my-auto w-[calc(var(--section-width)-200px)] mx-auto max-w-full'>
                <VideoPlayerContent url={threeDUrl}></VideoPlayerContent>
              </div>
            )
          }
          {
            showVideo && (
              <div className='my-auto w-[calc(var(--section-width)-200px)] mx-auto max-w-full'>
                <VideoPlayerContent url={videoUrl}></VideoPlayerContent>
              </div>
            )
          }
          {
            !!currentTag && (
              <GridView wrap={contentRef.current}></GridView>
            )
          }
          {
            !currentTag && !showThreeD && !showVideo && (
              <ListView></ListView>
            )
          }
          <aside>
            <GalleryAside className={classNames({ 'sticky top-0': !!currentTag })}></GalleryAside>
          </aside>
        </div>
      </div>
      {
        agent && (
          <div className='shadow-card p-10 hidden xs-s:block'>
            <AgentEmailButton data={agent} house={house}>Enquire</AgentEmailButton>
          </div>
        )
      }
    </div>
  )
}

function TopBar() {
  const {
    imageTagTidy,
    currentTag,
    showThreeD,
    showVideo,
    threeDUrl,
    videoUrl,
  } = useGallery()

  const allTag = useMemo(() => {
    const res = [
      { label: 'All', id: 'all', all: true },
      ...imageTagTidy
    ]
    if (threeDUrl) {
      res.push({ label: '3D Virtual tour', id: '3d', threeD: true })
    }
    if (videoUrl) {
      res.push({ label: 'Video', id: 'video', video: true })
    }
    return res
  }, [imageTagTidy, threeDUrl, videoUrl])

  return (
    <div className='flex-1 min-w-0 pt-30 flex items-end pr-30'>
      <div className={classNames('gap-x-20 whitespace-nowrap overflow-auto flex')} id='tag-wrap'>
        {
          map(allTag, (item, idx) => (
            <ButtonItem
              item={item}
              active={(!currentTag && idx === 0 && !showThreeD && !showVideo) || isEqual(currentTag, item) || (showThreeD && item.threeD) || (showVideo && item.video)}
              key={`${item.id}-${idx}`}
            ></ButtonItem>
          ))
        }
      </div>
    </div>
  )
}

function ButtonItem({
  item,
  active = false
}) {
  const {
    size: totals,
    currentTag,
    changeCurrentTag,
    displayThreeD,
    displayVideo
  } = useGallery()

  const clickHandle = useCallback(() => {
    if (item.all) {
      changeCurrentTag()
    } else if (item.video) {
      displayVideo()
    } else if (item.threeD) {
      displayThreeD()
    } else {
      if (!currentTag) {
        changeCurrentTag({})
      }
      setTimeout(() => {
        const el = document.getElementById(item.id)
        if (el) el.scrollIntoView()
      }, 0)
    }
  }, [item, currentTag, changeCurrentTag, displayVideo, displayThreeD])

  return (
    <button
      type='button'
      className={classNames('border-b-4 text-lg pb-22', active ? 'border-primary text-primary' : 'border-transparent text-black')}
      onClick={clickHandle}
      id={`tag-${item.id}`}
    >
      <span className='hover:bg-neutral-1 px-10 h-20 flex place-items-center rounded-[100px] whitespace-nowrap text-base'>
        {item.label}
        {
          (item.all || size(item.images) > 0) && (
            <span className={classNames('ml-4 text-sm', { 'text-neutral-5': !active })}>({item.all ? totals : size(item.images)})</span>
          )
        }
      </span>
    </button>
  )
}

function ListView() {
  const {
    images,
    floorPlanImages,
    index,
    address,
  } = useGallery()

  return (
    <GallerySlide
      images={images}
      floorPlanImages={floorPlanImages}
      index={index}
      alt={address}
    ></GallerySlide>
  )
}

function GridView({
  wrap
}) {
  const {
    imageTagTidy,
    changeCurrentTag
  } = useGallery()

  const [insideItems, { updateAt: updateInsideItem }] = useList([])
  const removeInsideItem = useCallback((idx) => {
    updateInsideItem(idx, undefined)
  }, [updateInsideItem])

  const pushInsideItem = useCallback((idx, item) => {
    updateInsideItem(idx, item)
  }, [updateInsideItem])

  useEffect(() => {
    const firstItem = find(insideItems, item => !!item)
    if (!firstItem) return

    setTimeout(() => {
      const el = document.getElementById(`tag-${firstItem.id}`)
      if (el) {
        const wrap = document.getElementById('tag-wrap')
        document.getElementById('tag-wrap').scrollTo({ left: el.offsetLeft - wrap.offsetWidth / 2 + el.offsetWidth / 2, behavior: 'smooth' })
      }
    }, 100)
    changeCurrentTag(firstItem)
  }, [insideItems, changeCurrentTag])

  return (
    <div className='min-w-0'>
      <div className='flex flex-col gap-y-60 lg:gap-y-96 w-[calc(var(--section-width)-200px)] mx-auto max-w-full'>
        {
          map(imageTagTidy, (item, idx) => (
            <GridItem
              data={item}
              key={idx}
              odd={idx % 2 === 0}
              push={() => pushInsideItem(idx, item)}
              remove={() => removeInsideItem(idx, item)}
              style={idx === size(imageTagTidy) - 1 ? { minHeight: get(wrap, 'offsetHeight', 0) - 96 } : undefined}
            ></GridItem>
          ))
        }
      </div>
    </div>
  )
}

function GridItem({
  data,
  push,
  remove,
  odd = false,
  style
}) {

  const {
    jumpToList,
  } = useGallery()
  const clickHandle = useCallback((src) => () => jumpToList(src), [jumpToList])

  const isThree = size(data.images) === 3
  const isFive = size(data.images) === 5
  return (
    <Waypoint onEnter={push} onLeave={remove}>
      <div id={data.id} className='first:pt-36' style={style}>
        <div className='text-xl text-black'>{data.label}</div>
        <div className='grid gap-12 grid-cols-1 lg:grid-cols-2 mt-16'>
          {
            map(data.images, (src, idx) => (
              <div
                key={idx}
                className={classNames(
                  'aspect-[860/420] cursor-pointer',
                  { 'lg:col-start-1 lg:col-end-3': idx % 3 === 0 && odd && !isThree && !isFive },
                  { 'lg:col-start-1 lg:col-end-3': idx % 3 === 2 && !odd && !isThree && !isFive },
                  { 'lg:row-start-1 lg:row-end-3 !aspect-auto': idx === 0 && isThree },
                  { 'lg:row-start-2 lg:row-end-4 !aspect-auto': idx === 2 && isFive }
                )}>
                <ResponsiveImageBgLoading
                  src={src}
                  width={1020}
                  height={605}
                  mobileWidth={1020}
                  mobileHeight={605}
                  alt={`${data.label}_${idx}`}
                  className='w-full h-full object-cover block'
                  onClick={clickHandle(src)}
                ></ResponsiveImageBgLoading>
              </div>
            ))
          }
        </div>
      </div>
    </Waypoint>
  )
}
